import "@arco-design/web-react/es/Modal/style";
import _Modal from "@arco-design/web-react/es/Modal";
import "@arco-design/web-react/es/Spin/style";
import _Spin from "@arco-design/web-react/es/Spin";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FileViewer from '@file-viewer/react-legacy';
// office preset：包含 docx/xlsx/pptx/pdf/ofd 等渲染器。
// preset 必须通过 options.preset 显式注入才能被 FileViewer 使用，
// 单纯 import 只是声明依赖，不会自动注册。
import officePreset from '@file-viewer/preset-office';
import archiveRenderer from '@file-viewer/renderer-archive';
import textRenderer from '@file-viewer/renderer-text';
// 音视频 renderer：mp4 / webm / mp3 / hls / midi 等格式的预览能力
import mediaRenderer from '@file-viewer/renderer-media';
import './index.less';
/**
 * 文件预览弹窗插件。
 *
 * 在 Modal 里嵌入 <FileViewer />，并把 `@file-viewer/preset-office` 自注册到全局
 * 注册表，使 docx / xlsx / pptx 等 Office 文件能直接渲染。
 */
export default function FileViewerModal(props) {
  const {
    visible,
    title = '文件预览',
    url,
    file,
    buffer,
    filename,
    mimeType,
    loading = true,
    width = 1350,
    onCancel
  } = props;

  // 把"加载态"独立成本地状态：组件 mount / visible 由 false→true 后开始 fetch，
  // 真正加载完成由 onStateChange 的 'loaded' 事件关闭 loading。
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (visible) {
      setBusy(loading);
    }
  }, [visible, loading]);

  // 用 file / buffer / url 三选一，避免空字段进入 viewer。
  // 优先 file（Blob）：不依赖 ArrayBuffer，PDF worker transfer 时不会 detach 原 buffer。
  // buffer 必须拷贝一份：worker 取走 ownership 后原 buffer 就变成 detached，
  // 重新打开或预览其他文件时就会抛 "Cannot perform Construct on a detached ArrayBuffer"。
  // 必须 useMemo：每次 render 都重建 source 对象会让 FileViewer 检测到 url 变化触发重新 fetch。
  const source = useMemo(() => {
    if (file) {
      return {
        file
      };
    }
    if (buffer) {
      return {
        file: new Blob([buffer.slice(0)], {
          type: mimeType || 'application/octet-stream'
        })
      };
    }
    if (url) {
      return {
        url
      };
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, buffer, url, mimeType]);

  // options / onStateChange 用 useMemo/useCallback 稳定引用，
  // 否则 FileViewer 每次 render 都拿到新对象，会判定数据变化触发重新 fetch。
  const viewerOptions = useMemo(() => ({
    locale: 'zh-CN',
    preset: officePreset,
    renderers: [archiveRenderer, textRenderer, mediaRenderer],
    pdf: {
      defaultNavigationVisible: false
    },
    fit: {
      mode: 'actual',
      minScale: /\.xl(sx?|sm|sb)$/i.test(filename) || /excel|spreadsheet/i.test(mimeType || '') ? 1.2 : 1.0
    }
  }), [filename, mimeType]);
  const handleStateChange = useCallback(s => {
    if (s.ready || s.error) {
      setBusy(false);
    }
  }, []);
  return /*#__PURE__*/React.createElement(_Modal, {
    className: "lx-file-view-modal",
    title: title,
    visible: visible,
    onCancel: onCancel,
    footer: /*#__PURE__*/React.createElement(React.Fragment, null),
    okText: null,
    cancelText: "\u5173\u95ED",
    maskClosable: false,
    alignCenter: false,
    style: {
      width,
      top: '8vh'
    },
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      width: '100%',
      height: 'calc(84vh - 48px - 20px)',
      background: '#fafafa'
    }
  }, busy && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      inset: 0,
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.7)'
    }
  }, /*#__PURE__*/React.createElement(_Spin, {
    tip: "\u52A0\u8F7D\u4E2D\u2026"
  })), visible && /*#__PURE__*/React.createElement(FileViewer, _extends({
    key: url
  }, source, {
    filename: filename,
    type: mimeType,
    name: filename,
    options: viewerOptions,
    onStateChange: handleStateChange,
    style: {
      width: '100%',
      height: '100%'
    }
  }))));
}